import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import api from '../services/api';
import SafeImg from '../components/SafeImg';
import { normalizeListForAdmin, toBool } from '../utils/normalizeApi';
import { readValidatedImageAsDataUrl } from '../utils/imageUpload';

function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    department: '',
    qualification: '',
    experience: '',
    email: '',
    phone: '',
    photoUrl: '',
    isActive: true
  });

  const [photoFile, setPhotoFile] = useState(null);

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      const response = await api.get('/staff/all');
      setStaff(normalizeListForAdmin(response.data));
    } catch (error) {
      console.error('Error fetching staff:', error);
      setStaff([
        { id: 1, name: 'Dr. Rajesh Kumar', designation: 'Principal', department: 'Administration', qualification: 'Ph.D. in Education', experience: '25+ years', email: '', phone: '', photoUrl: '', isActive: true }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Always send photoUrl - either new file or existing
      const submitData = { ...formData };
      if (editingId) {
        // Update - send all fields including photoUrl
        await api.put(`/staff/${editingId}`, submitData);
        alert('✅ Staff member updated successfully!');
      } else {
        // Add new staff
        await api.post('/staff', submitData);
        alert('✅ Staff member added successfully!');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      setPhotoFile(null);
      fetchStaff();
    } catch (error) {
      console.error('Staff CRUD Error:', error);
      const d = error.response?.data;
      const errorMsg = d?.message || d?.error || (typeof d === 'string' ? d : error.message) || 'Unknown error';
      alert('❌ Error: ' + errorMsg);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    try {
      const dataUrl = await readValidatedImageAsDataUrl(file);
      setFormData((prev) => ({ ...prev, photoUrl: dataUrl }));
    } catch (err) {
      alert(err.message || 'Invalid image');
      e.target.value = '';
      setPhotoFile(null);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.name || '',
      designation: item.designation || item.position || '',
      department: item.department || '',
      qualification: item.qualification || item.qualifications || '',
      experience: item.experience || item.bio || '',
      email: item.email || '',
      phone: item.phone || '',
      photoUrl: item.photoUrl || item.imageUrl || '',
      isActive: toBool(item.isActive, true)
    });
    setPhotoFile(null); // Reset file input
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this staff member?')) return;
    try { await api.delete(`/staff/${id}`); alert('✅ Staff deleted successfully!'); fetchStaff(); }
    catch (error) { console.error('Delete error:', error); alert('❌ Error deleting staff'); }
  };

  const resetForm = () => {
    setFormData({ name: '', designation: '', department: '', qualification: '', experience: '', email: '', phone: '', photoUrl: '', isActive: true });
    setPhotoFile(null);
  };

  if (loading) return <AdminLayout><div className="p-6">Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-blue-900">Staff Management</h1>
          <button onClick={() => setShowForm(true)}
            className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition flex items-center gap-2">
            <FaPlus /> Add Staff
          </button>
        </motion.div>

        {showForm && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">{editingId ? 'Edit' : 'Add'} Staff</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="Full Name" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Designation *</label>
                    <input type="text" value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="e.g., Principal" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Department *</label>
                    <input type="text" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="e.g., Mathematics" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Qualification</label>
                    <input type="text" value={formData.qualification} onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="e.g., M.Sc, Ph.D." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Experience</label>
                    <input type="text" value={formData.experience} onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="e.g., 10+ years" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="name@school.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input type="text" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="e.g., 9876543210" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Photo</label>
                  <input type="file" accept="image/*" onChange={handlePhotoChange}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" />
                  {formData.photoUrl && (
                    <div className="mt-2">
                      <SafeImg src={formData.photoUrl} alt="Preview" className="max-w-xs rounded-lg shadow-md" />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 text-blue-900 rounded focus:ring-blue-900" />
                  <label className="text-sm text-gray-700">Active</label>
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="submit" className="flex-1 bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition font-semibold">
                    {editingId ? 'Update' : 'Add'} Staff
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition font-semibold">Cancel</button>
                </div>
              </form>
            </div>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {staff.map((item) => (
              <div key={item.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {(item.photoUrl || item.imageUrl) && (
                  <SafeImg
                    key={(item.photoUrl || item.imageUrl || '') + item.id}
                    src={item.photoUrl || item.imageUrl}
                    alt={item.name}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-4">
                  <h3 className="font-bold text-lg text-blue-900">{item.name}</h3>
                  <p className="text-sm text-gray-600">{item.designation || item.position}</p>
                  {item.department && (<p className="text-sm text-gray-500">{item.department}</p>)}
                  {(item.email || item.phone) && (
                    <p className="text-xs text-gray-500 mt-1">{item.email}{item.email && item.phone ? ' · ' : ''}{item.phone}</p>
                  )}
                  <div className="flex justify-between items-center mt-3">
                    <span className={`px-2 py-1 rounded text-xs ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(item)} className="text-blue-900 hover:text-blue-700"><FaEdit size={18} /></button>
                      <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800"><FaTrash size={18} /></button>
                    </div>
                  </div>
                </div>
              </div>
          ))}
        </motion.div>
      </div>
    </AdminLayout>
  );
}

export default StaffManagement;
